import React, { useEffect, useRef, useCallback, useState } from 'react';
import { fetchGraph } from '../api';

const DEFAULT_SETTINGS = {
  repulsion: 1100,
  hierarchySpring: 0.02,
  explicitSpring: 0.0006,
  damping: 0.87,
  centerForce: 0.0007,
  hierarchyDistance: 110,
  explicitDistance: 220,
  clusterRepulsion: 3.8,
  clusterGravity: 0.010,
  nodeSize: 1,
  lineWidth: 1,
  textOpacity: 0.7,
};

// Cluster color palette
const CLUSTER_COLORS = [
  '#1A6B5A', '#B8762B', '#6B5B8A', '#B54B45', '#3D8C5C',
  '#C4713B', '#4A7A8C', '#8C6B4A', '#5B7A5B', '#7A5B6B',
  '#4A6B8C', '#8C5B4A', '#6B8C5B', '#5B4A8C', '#8C7A4A',
];

function Slider({ label, value, min, max, step, onChange }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
        <span style={{ fontSize: '0.78rem', color: 'var(--color-text-secondary)' }}>{label}</span>
        <span style={{ fontSize: '0.7rem', color: 'var(--color-text-tertiary)', fontFamily: 'var(--font-mono)' }}>
          {typeof value === 'number' ? (value < 0.01 ? value.toFixed(4) : value < 1 ? value.toFixed(2) : value.toFixed(0)) : value}
        </span>
      </div>
      <input type="range" min={min} max={max} step={step} value={value}
        onChange={e => onChange(parseFloat(e.target.value))}
        style={{ width: '100%', accentColor: 'var(--color-action)' }}
      />
    </div>
  );
}

// Get root cluster ID for a Luhmann ID (e.g., "1a3b" → "1")
function getRootId(id) {
  const match = id.match(/^(\d+)/);
  return match ? match[1] : id;
}

// Get all descendants of a node via hierarchy links
function getDescendants(nodeId, links, nodeMap) {
  const result = new Set();
  const queue = [nodeId];
  while (queue.length > 0) {
    const current = queue.shift();
    for (const l of links) {
      if (l.type === 'hierarchy' && l.src.id === current && !result.has(l.tgt.id)) {
        result.add(l.tgt.id);
        queue.push(l.tgt.id);
      }
    }
  }
  return result;
}

function GraphView({ onSelectZettel, visible, selectedId, graphLinkEvent }) {
  const canvasRef = useRef(null);
  const [showSettings, setShowSettings] = useState(false);
  const [settings, setSettings] = useState(() => {
    try {
      const saved = localStorage.getItem('graph-settings-v7');
      return saved ? { ...DEFAULT_SETTINGS, ...JSON.parse(saved) } : DEFAULT_SETTINGS;
    } catch { return DEFAULT_SETTINGS; }
  });

  const settingsRef = useRef(settings);
  useEffect(() => {
    settingsRef.current = settings;
    localStorage.setItem('graph-settings-v7', JSON.stringify(settings));
  }, [settings]);

  const selectedIdRef = useRef(selectedId);
  useEffect(() => { selectedIdRef.current = selectedId; }, [selectedId]);

  // PERF-001 Fix: rAF-Loop nur laufen lassen wenn Graph sichtbar.
  // rafIdRef speichert die aktive requestAnimationFrame-ID zum gezielten Abbruch.
  const rafIdRef = useRef(null);
  const visibleRef = useRef(visible);
  useEffect(() => {
    visibleRef.current = visible;
  }, [visible]);

  const set = (key, val) => setSettings(prev => ({ ...prev, [key]: val }));

  const stateRef = useRef({
    nodes: [], links: [], nodeMap: {},
    zoom: 1, panX: 0, panY: 0,
    drag: null, dragStartX: 0, dragStartY: 0, dragMoved: false,
    dragDescendants: new Set(),
    isPanning: false, panStart: null,
    hovered: null,
    hoverBlend: 0,  // 0 = no hover effect, 1 = full hover effect; lerps smoothly
    selectedBlend: 0,  // smooth blend for selected node highlight
    alpha: 1.0,  // simulation "temperature" — cools down to stop jittering
    isWarmedUp: false
  });

  const graphDataRef = useRef(null);
  const initializedRef = useRef(false);

  // Lazy: Graph-Daten erst laden wenn sichtbar (statt sofort beim Mount)
  useEffect(() => {
    if (!visible || initializedRef.current) return;
    const loadAndInit = async () => {
      if (!graphDataRef.current) {
        try { graphDataRef.current = await fetchGraph(); } catch { return; }
      }
      initGraph(graphDataRef.current);
    };
    const timer = setTimeout(loadAndInit, 100);
    return () => clearTimeout(timer);
  }, [visible]); // eslint-disable-line

  const initGraph = (data) => {
    if (!data || initializedRef.current) return;
    initializedRef.current = true;
    const s = stateRef.current;
    const canvas = canvasRef.current;
    const W = canvas?.parentElement?.clientWidth || 800;
    const H = canvas?.parentElement?.clientHeight || 600;

    // Assign clusters
    const roots = [...new Set(data.nodes.map(n => getRootId(n.id)))].sort((a, b) => Number(a) - Number(b));
    const rootIndex = {};
    roots.forEach((r, i) => { rootIndex[r] = i; });

    s.nodes = data.nodes.map((n) => {
      const root = getRootId(n.id);
      return { ...n, root, clusterIndex: rootIndex[root] || 0, x: W / 2, y: H / 2, vx: 0, vy: 0, depth: 0 };
    });

    s.nodeMap = {};
    for (const nd of s.nodes) s.nodeMap[nd.id] = nd;

    s.links = data.links.map(l => ({
      ...l,
      src: s.nodeMap[l.source],
      tgt: s.nodeMap[l.target]
    })).filter(l => l.src && l.tgt);

    // Build hierarchy maps
    s._parentOf = {};
    s._childrenMap = {};
    for (const l of s.links) {
      if (l.type === 'hierarchy') {
        s._parentOf[l.tgt.id] = l.src.id;
        if (!s._childrenMap[l.src.id]) s._childrenMap[l.src.id] = [];
        s._childrenMap[l.src.id].push(l.tgt.id);
      }
    }

    // Compute depth for each node
    for (const nd of s.nodes) {
      let depth = 0, cur = nd.id;
      while (s._parentOf[cur]) { depth++; cur = s._parentOf[cur]; }
      nd.depth = depth;
    }

    // Count total descendants for angle allocation
    const descCount = {};
    function countDesc(id) {
      const children = s._childrenMap[id] || [];
      let total = 1; // count self
      for (const c of children) total += countDesc(c);
      descCount[id] = total;
      return total;
    }
    // Root nodes in the hierarchy (no parent)
    const hierarchyRoots = s.nodes.filter(n => !s._parentOf[n.id]);
    for (const r of hierarchyRoots) countDesc(r.id);

    // Radial tree positioning — place nodes in concentric rings by depth
    const cfg = settingsRef.current;
    const ringSpacing = cfg.hierarchyDistance * 2.0;
    const cx = W / 2, cy = H / 2;

    function positionTree(nodeId, centerAngle, angleSpan, depth) {
      const node = s.nodeMap[nodeId];
      if (!node) return;
      // Give root nodes (depth=0) a minimum radius so they don't all collapse
      // to the same point — repulsion needs a direction to work from
      const minRootRadius = ringSpacing * (hierarchyRoots.length > 1 ? 0.55 : 0);
      const r = depth === 0 ? minRootRadius : depth * ringSpacing;
      node.x = cx + Math.cos(centerAngle) * r;
      node.y = cy + Math.sin(centerAngle) * r;

      const children = s._childrenMap[nodeId] || [];
      if (children.length === 0) return;

      // Allocate angle proportional to descendant count
      const totalWeight = children.reduce((sum, c) => sum + (descCount[c] || 1), 0);
      let currentAngle = centerAngle - angleSpan / 2;
      for (const childId of children) {
        const weight = (descCount[childId] || 1) / totalWeight;
        const childSpan = angleSpan * weight;
        const childCenter = currentAngle + childSpan / 2;
        positionTree(childId, childCenter, childSpan, depth + 1);
        currentAngle += childSpan;
      }
    }

    // Position each root cluster — give minimum angle to small clusters
    const minAnglePerRoot = (Math.PI * 2) / (hierarchyRoots.length * 2.5); // minimum angle per root
    // First pass: compute raw weights
    const rawWeights = hierarchyRoots.map(r => Math.sqrt(descCount[r.id] || 1)); // sqrt for more even distribution
    const rawTotal = rawWeights.reduce((a, b) => a + b, 0);
    // Second pass: ensure minimum angle
    const angles = rawWeights.map(w => Math.max(minAnglePerRoot, (w / rawTotal) * Math.PI * 2));
    const angleTotal = angles.reduce((a, b) => a + b, 0);
    // Normalize to exactly 2*PI
    const scale = (Math.PI * 2) / angleTotal;
    let startAngle = -Math.PI / 2;
    for (let i = 0; i < hierarchyRoots.length; i++) {
      const span = angles[i] * scale;
      const center = startAngle + span / 2;
      positionTree(hierarchyRoots[i].id, center, span * 0.82, 0);
      startAngle += span;
    }

    // PERF-002 Fix: Physics-Warmup in Chunks aufteilen statt 600 synchrone Ticks.
    // Chunk-Größe 100 Ticks, 6 Chunks — Hauptthread bleibt zwischen den Chunks frei.
    s.alpha = 1.0;
    const CHUNK = 100;
    const TOTAL_TICKS = 600;
    let done = 0;
    const runChunk = () => {
      const end = Math.min(done + CHUNK, TOTAL_TICKS);
      for (; done < end; done++) {
        simulateStep(s, cfg, W, H, true);
      }
      if (done < TOTAL_TICKS) {
        setTimeout(runChunk, 0);
      } else {
        for (const nd of s.nodes) { nd.vx = 0; nd.vy = 0; }
        s.alpha = 0.3;
        s.isWarmedUp = true;
        if (selectedIdRef.current && s.nodeMap[selectedIdRef.current]) {
          focusNode(selectedIdRef.current);
        } else {
          fitToView();
        }
      }
    };
    setTimeout(runChunk, 0);
  };

  // Fit all nodes into the visible canvas area with padding
  const fitToView = (animate = true) => {
    const s = stateRef.current;
    const canvas = canvasRef.current;
    if (!canvas || s.nodes.length === 0) return;
    const W = canvas.parentElement.clientWidth;
    const H = canvas.parentElement.clientHeight;

    let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
    for (const n of s.nodes) {
      minX = Math.min(minX, n.x); maxX = Math.max(maxX, n.x);
      minY = Math.min(minY, n.y); maxY = Math.max(maxY, n.y);
    }
    const graphW = maxX - minX || 1;
    const graphH = maxY - minY || 1;
    const padding = 60;
    const zoom = Math.min((W - padding * 2) / graphW, (H - padding * 2) / graphH, 2.0);
    const centerX = (minX + maxX) / 2;
    const centerY = (minY + maxY) / 2;
    const panX = W / 2 - centerX;
    const panY = H / 2 - centerY;

    if (animate) {
      // Smooth animated transition
      s._animTarget = { zoom, panX, panY };
      s._animating = true;
    } else {
      s.zoom = zoom;
      s.panX = panX;
      s.panY = panY;
    }
  };

  // Focus (pan+zoom) to a specific node smoothly
  const focusNode = (nodeId) => {
    const s = stateRef.current;
    const canvas = canvasRef.current;
    if (!canvas || !s.nodeMap[nodeId]) return;
    const W = canvas.parentElement.clientWidth;
    const H = canvas.parentElement.clientHeight;
    const node = s.nodeMap[nodeId];
    const panX = W / 2 - node.x;
    const panY = H / 2 - node.y;
    // Zoom to a comfortable level if too zoomed out
    const targetZoom = Math.max(s.zoom, 0.8);
    s._animTarget = { zoom: targetZoom, panX, panY };
    s._animating = true;
  };

  // When selectedId changes while graph is visible, always focus on the node
  useEffect(() => {
    if (!selectedId || !visible || !initializedRef.current) return;
    const node = stateRef.current.nodeMap[selectedId];
    if (!node) return;
    focusNode(selectedId);
  }, [selectedId]); // eslint-disable-line

  // When switching to graph view, focus on selected note or fit to view.
  // Delay by the layout transition duration (400ms) so the canvas has its
  // final dimensions before we compute zoom/pan — otherwise fitToView
  // measures a mid-transition (too-small) panel and ends up under-zoomed.
  useEffect(() => {
    if (!visible || !initializedRef.current) return;
    const timer = setTimeout(() => {
      if (selectedIdRef.current && stateRef.current.nodeMap[selectedIdRef.current]) {
        focusNode(selectedIdRef.current);
      } else {
        fitToView();
      }
    }, 420); // slightly longer than --transition-layout (400ms)
    return () => clearTimeout(timer);
  }, [visible]); // eslint-disable-line

  // Extracted simulation step for reuse in warmup and frame loop
  const simulateStep = (s, cfg, W, H, isWarmup = false) => {
    const { nodes, links } = s;

    // Alpha cooling
    if (!isWarmup && !s.drag) {
      s.alpha *= 0.997;
      if (s.alpha < 0.005) s.alpha = 0.005;
    }
    const a = isWarmup ? 1.0 : s.alpha;
    if (a < 0.006 && !isWarmup) return;

    // Cache connection counts
    if (!s._connCounts) {
      s._connCounts = {};
      for (const n of nodes) s._connCounts[n.id] = 0;
      for (const l of links) { s._connCounts[l.src.id]++; s._connCounts[l.tgt.id]++; }
    }
    const cc2 = s._connCounts;

    // 1) Repulsion — weaker within same cluster to preserve radial structure
    for (let i = 0; i < nodes.length; i++) {
      for (let j = i + 1; j < nodes.length; j++) {
        const na = nodes[i], nb = nodes[j];
        const dx = nb.x - na.x;
        const dy = nb.y - na.y;
        const d2 = dx * dx + dy * dy;
        const dist = Math.sqrt(d2) || 1;
        const isolationDamper = (cc2[na.id] <= 1 || cc2[nb.id] <= 1) ? 0.3 : 1.0;
        const sameCluster = na.root === nb.root;
        const clusterMul = sameCluster ? 0.7 : cfg.clusterRepulsion;
        const f = (cfg.repulsion * clusterMul * isolationDamper) / (d2 + 200) * a;
        const fx = (dx / dist) * f;
        const fy = (dy / dist) * f;
        na.vx -= fx; na.vy -= fy;
        nb.vx += fx; nb.vy += fy;
      }
    }

    // 2) Hierarchy springs — pull parent-child toward target distance
    for (const l of links) {
      const dx = l.tgt.x - l.src.x;
      const dy = l.tgt.y - l.src.y;
      const dist = Math.sqrt(dx * dx + dy * dy) || 1;
      const isHierarchy = l.type === 'hierarchy';
      const targetDist = isHierarchy ? cfg.hierarchyDistance : cfg.explicitDistance;
      const springK = isHierarchy ? cfg.hierarchySpring : cfg.explicitSpring;
      const f = (dist - targetDist) * springK * a;
      const fx = (dx / dist) * f;
      const fy = (dy / dist) * f;
      l.src.vx += fx; l.src.vy += fy;
      l.tgt.vx -= fx; l.tgt.vy -= fy;
    }

    // 3) Radial depth force — push children outward from center relative to parent
    //    Ensures hierarchy levels form concentric rings
    const cxW = W / 2, cyH = H / 2;
    if (s._parentOf) {
      for (const n of nodes) {
        if (s.drag && s.drag.id === n.id) continue;
        const parentId = s._parentOf[n.id];
        if (!parentId) continue;
        const parent = s.nodeMap[parentId];
        if (!parent) continue;

        // Child should be further from center than parent
        const parentDist = Math.sqrt((parent.x - cxW) ** 2 + (parent.y - cyH) ** 2);
        const nodeDist = Math.sqrt((n.x - cxW) ** 2 + (n.y - cyH) ** 2);
        const targetDist = parentDist + cfg.hierarchyDistance;

        if (nodeDist < targetDist) {
          // Push outward from center
          const angle = Math.atan2(n.y - cyH, n.x - cxW);
          const push = (targetDist - nodeDist) * 0.015 * a;
          n.vx += Math.cos(angle) * push;
          n.vy += Math.sin(angle) * push;
        }
      }
    }

    // 4) Angular sibling ordering — spread children evenly around parent
    if (s._childrenMap) {
      for (const parentId of Object.keys(s._childrenMap)) {
        const childIds = s._childrenMap[parentId];
        if (childIds.length < 2) continue;
        const parent = s.nodeMap[parentId];
        if (!parent) continue;

        // Get child nodes
        const children = childIds.map(id => s.nodeMap[id]).filter(Boolean);

        // Sort children by their current angle relative to parent
        const withAngle = children.map(c => ({
          node: c,
          angle: Math.atan2(c.y - parent.y, c.x - parent.x)
        }));
        withAngle.sort((a, b) => a.angle - b.angle);

        // Apply angular separation force between adjacent children
        for (let i = 0; i < withAngle.length; i++) {
          const curr = withAngle[i];
          const next = withAngle[(i + 1) % withAngle.length];
          let angleDiff = next.angle - curr.angle;
          if (angleDiff < 0) angleDiff += Math.PI * 2;

          const minAngle = (Math.PI * 2) / (withAngle.length * 1.2);
          if (angleDiff < minAngle) {
            // Push apart angularly
            const torque = (minAngle - angleDiff) * 0.3 * a;
            const cDist = Math.sqrt((curr.node.x - parent.x) ** 2 + (curr.node.y - parent.y) ** 2) || 1;
            const nDist = Math.sqrt((next.node.x - parent.x) ** 2 + (next.node.y - parent.y) ** 2) || 1;
            // Push curr clockwise, next counter-clockwise (perpendicular to radial)
            curr.node.vx += Math.sin(curr.angle) * torque * cDist * 0.02;
            curr.node.vy -= Math.cos(curr.angle) * torque * cDist * 0.02;
            next.node.vx -= Math.sin(next.angle) * torque * nDist * 0.02;
            next.node.vy += Math.cos(next.angle) * torque * nDist * 0.02;
          }
        }
      }
    }

    // 5) Center gravity + damping + velocity threshold
    for (const n of nodes) {
      if (s.drag && s.drag.id === n.id) continue;
      if (s.drag && s.dragDescendants.has(n.id)) {
        n.vx *= 0.7;
        n.vy *= 0.7;
      }
      // Center gravity — roots pulled to center, leaves less
      const depthFactor = n.depth === 0 ? 4 : (n.depth <= 1 ? 1.5 : 0.5);
      const nConns = cc2[n.id] || 0;
      const isolationFactor = nConns <= 1 ? 3 : 1;
      n.vx += (cxW - n.x) * cfg.centerForce * depthFactor * isolationFactor * a;
      n.vy += (cyH - n.y) * cfg.centerForce * depthFactor * isolationFactor * a;

      n.vx *= cfg.damping;
      n.vy *= cfg.damping;
      if (Math.abs(n.vx) < 0.04 && Math.abs(n.vy) < 0.04) {
        n.vx = 0; n.vy = 0;
      }
      n.x += n.vx;
      n.y += n.vy;
    }
  };

  const frame = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const s = stateRef.current;
    const cfg = settingsRef.current;
    const { nodes, links } = s;

    const dpr = window.devicePixelRatio || 1;
    const W = canvas.parentElement.clientWidth;
    const H = canvas.parentElement.clientHeight;
    if (canvas.width !== W * dpr || canvas.height !== H * dpr) {
      canvas.width = W * dpr;
      canvas.height = H * dpr;
      canvas.style.width = W + 'px';
      canvas.style.height = H + 'px';
    }

    if (nodes.length > 0) {
      simulateStep(s, cfg, W, H);
    }

    // Smooth camera animation (fit-to-view, focus-on-node)
    if (s._animating && s._animTarget) {
      const t = s._animTarget;
      const speed = 0.1; // lerp speed per frame
      s.zoom += (t.zoom - s.zoom) * speed;
      s.panX += (t.panX - s.panX) * speed;
      s.panY += (t.panY - s.panY) * speed;
      // Stop animating when close enough
      if (Math.abs(t.zoom - s.zoom) < 0.002 && Math.abs(t.panX - s.panX) < 0.5 && Math.abs(t.panY - s.panY) < 0.5) {
        s.zoom = t.zoom; s.panX = t.panX; s.panY = t.panY;
        s._animating = false; s._animTarget = null;
      }
    }

    ctx.save();
    ctx.scale(dpr, dpr);
    ctx.clearRect(0, 0, W, H);
    ctx.translate(W / 2, H / 2);
    ctx.scale(s.zoom, s.zoom);
    ctx.translate(-W / 2 + s.panX, -H / 2 + s.panY);

    const cs = getComputedStyle(document.documentElement);
    const textPrimary = cs.getPropertyValue('--color-text').trim() || '#2C2C2C';
    const textMuted = cs.getPropertyValue('--color-text-tertiary').trim() || '#999';
    const divider = cs.getPropertyValue('--color-divider').trim() || '#ddd';
    const surfaceBg = cs.getPropertyValue('--color-surface').trim() || '#FFFFFF';
    const hasHover = s.hovered !== null;
    const currentSelectedId = selectedIdRef.current;

    // Smooth hover blend: lerp toward target (0 = no hover, 1 = full hover effect)
    const hoverTarget = hasHover ? 1 : 0;
    const lerpSpeed = 0.08; // lower = slower/smoother transition
    s.hoverBlend += (hoverTarget - s.hoverBlend) * lerpSpeed;
    if (Math.abs(s.hoverBlend - hoverTarget) < 0.005) s.hoverBlend = hoverTarget;
    const hb = s.hoverBlend; // shorthand

    // Smooth selected blend
    const selTarget = currentSelectedId ? 1 : 0;
    s.selectedBlend += (selTarget - s.selectedBlend) * 0.06;
    if (Math.abs(s.selectedBlend - selTarget) < 0.005) s.selectedBlend = selTarget;
    const sb = s.selectedBlend;

    // Helper: mix between normal and dimmed alpha
    const dimAlpha = (normalAlpha, dimmedAlpha) => normalAlpha + (dimmedAlpha - normalAlpha) * hb;

    // Draw links
    for (const l of links) {
      const connected = hasHover && (l.src.id === s.hovered || l.tgt.id === s.hovered);
      ctx.beginPath();
      ctx.moveTo(l.src.x, l.src.y);
      ctx.lineTo(l.tgt.x, l.tgt.y);
      if (connected) {
        ctx.strokeStyle = CLUSTER_COLORS[l.src.clusterIndex % CLUSTER_COLORS.length];
        ctx.lineWidth = (1.2 + 0.8 * hb) * cfg.lineWidth;
        ctx.globalAlpha = 0.35 + 0.45 * hb;
      } else {
        if (l.type === 'hierarchy') {
          ctx.strokeStyle = CLUSTER_COLORS[l.src.clusterIndex % CLUSTER_COLORS.length];
          ctx.lineWidth = (1.2 - 0.9 * hb) * cfg.lineWidth;
          ctx.globalAlpha = dimAlpha(0.35, 0.12);
        } else {
          ctx.strokeStyle = textMuted;
          ctx.lineWidth = (0.5 - 0.2 * hb) * cfg.lineWidth;
          ctx.globalAlpha = dimAlpha(0.15, 0.06);
        }
      }
      ctx.stroke();
      ctx.globalAlpha = 1;
    }

    // Draw nodes
    for (const n of nodes) {
      const isHovered = s.hovered === n.id;
      const isSelected = currentSelectedId === n.id;
      const isConnected = hasHover && links.some(l =>
        (l.src.id === s.hovered && l.tgt.id === n.id) ||
        (l.tgt.id === s.hovered && l.src.id === n.id)
      );
      const isDimmed = hasHover && !isHovered && !isConnected && !isSelected;
      const clusterColor = CLUSTER_COLORS[n.clusterIndex % CLUSTER_COLORS.length];
      const isRoot = n.id === n.root;
      const connectionCount = s._connCounts?.[n.id] || 0;
      const r = Math.max(3.5, Math.min(8, 3 + connectionCount * 0.6)) * cfg.nodeSize * (isRoot ? 1.4 : 1);

      // Selected node: subtle outer ring (less prominent than hover)
      if (isSelected && sb > 0.01 && !isHovered) {
        // Soft glow
        ctx.beginPath();
        ctx.arc(n.x, n.y, r + 12, 0, Math.PI * 2);
        ctx.fillStyle = clusterColor;
        ctx.globalAlpha = 0.06 * sb;
        ctx.fill();
        // Ring
        ctx.beginPath();
        ctx.arc(n.x, n.y, r + 5, 0, Math.PI * 2);
        ctx.strokeStyle = clusterColor;
        ctx.lineWidth = 1.5;
        ctx.globalAlpha = 0.35 * sb;
        ctx.stroke();
        ctx.globalAlpha = 1;
      }

      // Hover glow (always dominant, more prominent)
      if (isHovered && hb > 0.01) {
        ctx.beginPath();
        ctx.arc(n.x, n.y, r + 10, 0, Math.PI * 2);
        ctx.fillStyle = clusterColor;
        ctx.globalAlpha = 0.08 * hb;
        ctx.fill();
        ctx.globalAlpha = 1;
      }

      const hoverGrow = isHovered ? 1.5 * hb : (isSelected ? 0.8 * sb : 0);
      // Draw opaque background circle to hide lines behind nodes
      ctx.beginPath();
      ctx.arc(n.x, n.y, r + hoverGrow + 1, 0, Math.PI * 2);
      ctx.fillStyle = surfaceBg;
      ctx.globalAlpha = 1;
      ctx.fill();

      ctx.beginPath();
      ctx.arc(n.x, n.y, r + hoverGrow, 0, Math.PI * 2);
      if (isHovered) {
        ctx.fillStyle = clusterColor;
        ctx.globalAlpha = (isRoot ? 0.65 : 0.45) + (1 - (isRoot ? 0.65 : 0.45)) * hb;
      } else if (isSelected) {
        // Selected: elevated but not as strong as hover
        const baseAlpha = isRoot ? 0.65 : 0.45;
        ctx.fillStyle = clusterColor;
        ctx.globalAlpha = baseAlpha + (0.85 - baseAlpha) * sb;
      } else if (isConnected) {
        ctx.fillStyle = clusterColor;
        ctx.globalAlpha = (isRoot ? 0.65 : 0.45) + (0.7 - (isRoot ? 0.65 : 0.45)) * hb;
      } else if (isDimmed) {
        const normalAlpha = isRoot ? 0.65 : 0.45;
        const dimmedTo = 0.25;
        ctx.fillStyle = clusterColor;
        ctx.globalAlpha = dimAlpha(normalAlpha, dimmedTo);
      } else {
        ctx.fillStyle = clusterColor;
        ctx.globalAlpha = isRoot ? 0.65 : 0.45;
      }
      ctx.fill();
      ctx.globalAlpha = 1;

      // Title label
      const showLabel = (s.zoom > 0.5 && !(isDimmed && hb > 0.8)) || isHovered || isConnected || isSelected;
      if (showLabel) {
        const fs = isHovered ? 11 : (isSelected ? 10.5 : (isRoot ? 10 : 9));
        ctx.font = (isHovered || isSelected || isRoot) ? `600 ${fs}px -apple-system, sans-serif` : `${fs}px -apple-system, sans-serif`;
        ctx.textAlign = 'center';
        if (isDimmed) {
          ctx.globalAlpha = dimAlpha(cfg.textOpacity * 0.8, 0.2);
        } else if (isHovered) {
          ctx.globalAlpha = 0.8 + 0.2 * hb;
        } else if (isSelected) {
          ctx.globalAlpha = 0.85 * sb;
        } else {
          ctx.globalAlpha = cfg.textOpacity * 0.8;
        }
        ctx.fillStyle = (isHovered || isSelected) ? textPrimary : textMuted;
        const label = n.title.length > 20 ? n.title.substring(0, 18) + '…' : n.title;
        ctx.fillText(label, n.x, n.y + r + fs + 5);
        ctx.globalAlpha = 1;
      }

      // ID label
      if ((s.zoom > 0.5 && !(isDimmed && hb > 0.8)) || isHovered || isSelected) {
        ctx.font = `bold ${isRoot ? 9 : 7.5}px 'SF Mono', monospace`;
        ctx.fillStyle = clusterColor;
        ctx.globalAlpha = isDimmed ? dimAlpha(0.7, 0.25) : (isHovered ? 0.7 + 0.3 * hb : (isSelected ? 0.8 * sb : 0.7));
        ctx.fillText(n.id, n.x, n.y - r - 4);
        ctx.globalAlpha = 1;
      }
    }

    ctx.restore();
    // PERF-001 Fix: Loop nur weiterlaufen lassen wenn Graph sichtbar
    if (visibleRef.current) {
      rafIdRef.current = requestAnimationFrame(frame);
    } else {
      rafIdRef.current = null;
    }
  }, []); // eslint-disable-line

  useEffect(() => {
    if (visible) {
      // Graph wird sichtbar: Loop starten (sofern nicht schon läuft)
      if (!rafIdRef.current) {
        rafIdRef.current = requestAnimationFrame(frame);
      }
    } else {
      // Graph wird unsichtbar: Loop stoppen
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
    }
  }, [visible, frame]);

  // Echtzeit-Graph-Update: Knoten und Links inkrementell hinzufügen/entfernen ohne Refetch
  useEffect(() => {
    if (!graphLinkEvent || !initializedRef.current) return;
    const s = stateRef.current;
    const { added = [], removed = [], addedNodes = [], removedNodes = [] } = graphLinkEvent;
    const canvas = canvasRef.current;
    const W = canvas?.parentElement?.clientWidth || 800;
    const H = canvas?.parentElement?.clientHeight || 600;

    // Knoten entfernen (inkl. aller angrenzenden Links)
    if (removedNodes.length > 0) {
      const removeIds = new Set(removedNodes);
      s.links = s.links.filter(l => {
        const srcId = l.src?.id || l.source;
        const tgtId = l.tgt?.id || l.target;
        return !removeIds.has(srcId) && !removeIds.has(tgtId);
      });
      s.nodes = s.nodes.filter(n => !removeIds.has(n.id));
      for (const id of removeIds) delete s.nodeMap[id];
    }

    // Knoten hinzufügen
    for (const node of addedNodes) {
      if (s.nodeMap[node.id]) continue; // Duplikat-Check
      const root = getRootId(node.id);
      const newNode = {
        ...node, root, clusterIndex: 0,
        x: W / 2 + (Math.random() - 0.5) * 100,
        y: H / 2 + (Math.random() - 0.5) * 100,
        vx: 0, vy: 0, depth: 0
      };
      s.nodes.push(newNode);
      s.nodeMap[node.id] = newNode;
    }

    // Links entfernen
    if (removed.length > 0) {
      const removeSet = new Set(removed.map(r => `${r.source}->${r.target}`));
      s.links = s.links.filter(l => {
        const key = `${l.src?.id || l.source}->${l.tgt?.id || l.target}`;
        return !removeSet.has(key);
      });
    }

    // Links hinzufügen
    for (const link of added) {
      const src = s.nodeMap[link.source];
      const tgt = s.nodeMap[link.target];
      if (!src || !tgt) continue;
      const exists = s.links.some(l =>
        (l.src?.id || l.source) === link.source && (l.tgt?.id || l.target) === link.target
      );
      if (!exists) {
        s.links.push({ source: link.source, target: link.target, type: link.type || 'explicit', src, tgt });
        src.val = (src.val || 1) + 1;
      }
    }

    // Simulation leicht aufheizen für sanfte Neuanordnung
    s.alpha = Math.max(s.alpha, 0.3);

    // rAF-Loop starten falls er pausiert ist (Graph nicht sichtbar)
    if (!rafIdRef.current) {
      rafIdRef.current = requestAnimationFrame(frame);
    }
  }, [graphLinkEvent, frame]);

  const screenToWorld = (sx, sy) => {
    const W = canvasRef.current.parentElement.clientWidth;
    const H = canvasRef.current.parentElement.clientHeight;
    const s = stateRef.current;
    return {
      x: (sx - W / 2) / s.zoom + W / 2 - s.panX,
      y: (sy - H / 2) / s.zoom + H / 2 - s.panY
    };
  };

  const getNodeAt = (wx, wy) => {
    const cfg = settingsRef.current;
    for (const n of stateRef.current.nodes) {
      const r = Math.max(3.5, (n.val || 1) * 2) * cfg.nodeSize + 8;
      if ((wx - n.x) ** 2 + (wy - n.y) ** 2 < r * r) return n;
    }
    return null;
  };

  const handleWheel = (e) => {
    e.preventDefault();
    const s = stateRef.current;
    s._animating = false; // cancel any animation on user interaction
    const factor = e.deltaY > 0 ? 0.94 : 1.06;
    s.zoom = Math.max(0.15, Math.min(5, s.zoom * factor));
  };

  const handleMouseDown = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const { x, y } = screenToWorld(e.clientX - rect.left, e.clientY - rect.top);
    const node = getNodeAt(x, y);
    const s = stateRef.current;
    s._animating = false; // cancel any animation on user interaction
    if (node) {
      s.drag = node;
      s.dragStartX = node.x;
      s.dragStartY = node.y;
      s.dragMoved = false;
      s.alpha = Math.max(s.alpha, 0.5); // reheat so graph adapts to drag
      // Find all descendants for drag-follow
      s.dragDescendants = getDescendants(node.id, s.links, s.nodeMap);
    } else {
      s.isPanning = true;
      s.panStart = { x: e.clientX, y: e.clientY, px: s.panX, py: s.panY };
    }
  };

  const handleMouseMove = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const sx = e.clientX - rect.left;
    const sy = e.clientY - rect.top;
    const s = stateRef.current;
    if (s.drag) {
      const { x, y } = screenToWorld(sx, sy);
      const prevX = s.drag.x, prevY = s.drag.y;
      s.drag.x = x; s.drag.y = y; s.drag.vx = 0; s.drag.vy = 0;
      // Move descendants proportionally (weaker pull)
      const ddx = x - prevX, ddy = y - prevY;
      for (const descId of s.dragDescendants) {
        const desc = s.nodeMap[descId];
        if (desc) {
          desc.vx += ddx * 0.3;
          desc.vy += ddy * 0.3;
        }
      }
      const dx = x - s.dragStartX, dy = y - s.dragStartY;
      if (dx * dx + dy * dy > 25) s.dragMoved = true;
      return;
    }
    if (s.isPanning && s.panStart) {
      s.panX = s.panStart.px + (e.clientX - s.panStart.x) / s.zoom;
      s.panY = s.panStart.py + (e.clientY - s.panStart.y) / s.zoom;
      canvasRef.current.style.cursor = 'grabbing';
      return;
    }
    const { x, y } = screenToWorld(sx, sy);
    const node = getNodeAt(x, y);
    s.hovered = node ? node.id : null;
    canvasRef.current.style.cursor = node ? 'pointer' : 'grab';
  };

  const handleMouseUp = () => {
    const s = stateRef.current;
    if (s.drag) {
      if (!s.dragMoved) onSelectZettel(s.drag.id);
      if (s.dragMoved) s.alpha = Math.max(s.alpha, 0.3); // settle after drag
      s.drag = null;
      s.dragDescendants = new Set();
    }
    s.isPanning = false; s.panStart = null;
    if (canvasRef.current) canvasRef.current.style.cursor = 'grab';
  };

  return (
    <div className="graph-container" style={{ position: 'relative' }}>
      <canvas
        ref={canvasRef}
        onWheel={handleWheel}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={() => { const s = stateRef.current; s.drag = null; s.dragDescendants = new Set(); s.isPanning = false; s.hovered = null; }}
        style={{ width: '100%', height: '100%', cursor: 'grab' }}
      />

      {/* Fit-to-view button */}
      <button
        onClick={() => fitToView()}
        style={{
          position: 'absolute', top: 12, right: showSettings ? 312 : 52,
          width: 32, height: 32, borderRadius: 8,
          background: 'var(--color-surface)',
          border: '1px solid var(--color-divider)',
          color: 'var(--color-text-tertiary)',
          cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
          transition: 'right 0.2s, background 0.15s',
          boxShadow: 'var(--shadow-sm)'
        }}
        title="Übersicht (100%)"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="15 3 21 3 21 9"/>
          <polyline points="9 21 3 21 3 15"/>
          <line x1="21" y1="3" x2="14" y2="10"/>
          <line x1="3" y1="21" x2="10" y2="14"/>
        </svg>
      </button>

      {/* Settings button */}
      <button
        onClick={() => setShowSettings(!showSettings)}
        style={{
          position: 'absolute', top: 12, right: showSettings ? 272 : 12,
          width: 32, height: 32, borderRadius: 8,
          background: showSettings ? 'var(--color-primary-subtle)' : 'var(--color-surface)',
          border: '1px solid var(--color-divider)',
          color: showSettings ? 'var(--color-primary)' : 'var(--color-text-tertiary)',
          cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
          transition: 'right 0.2s, background 0.15s',
          boxShadow: 'var(--shadow-sm)'
        }}
        title="Graph-Einstellungen"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
        </svg>
      </button>

      {/* Settings Panel */}
      {showSettings && (
        <div style={{
          position: 'absolute', top: 0, right: 0, bottom: 0, width: 260,
          background: 'var(--color-surface)', borderLeft: '1px solid var(--color-divider)',
          padding: '16px 18px', overflowY: 'auto',
          boxShadow: '-4px 0 12px rgba(0,0,0,0.06)'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
            <span style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--color-text-tertiary)' }}>
              Cluster
            </span>
            <button onClick={() => setSettings(DEFAULT_SETTINGS)} style={{
              background: 'none', border: 'none', color: 'var(--color-text-tertiary)', cursor: 'pointer', fontSize: '0.75rem'
            }}>Zurücksetzen</button>
          </div>

          <Slider label="Hierarchie-Abstand" value={settings.hierarchyDistance} min={20} max={120} step={5} onChange={v => set('hierarchyDistance', v)} />
          <Slider label="Hierarchie-Kraft" value={settings.hierarchySpring} min={0.001} max={0.02} step={0.001} onChange={v => set('hierarchySpring', v)} />
          <Slider label="Querlink-Abstand" value={settings.explicitDistance} min={50} max={400} step={10} onChange={v => set('explicitDistance', v)} />
          <Slider label="Cluster-Abstoßung" value={settings.clusterRepulsion} min={1} max={5} step={0.1} onChange={v => set('clusterRepulsion', v)} />
          <Slider label="Cluster-Anziehung" value={settings.clusterGravity} min={0} max={0.03} step={0.001} onChange={v => set('clusterGravity', v)} />
          <Slider label="Abstoßungskraft" value={settings.repulsion} min={200} max={3000} step={50} onChange={v => set('repulsion', v)} />
          <Slider label="Zentrierkraft" value={settings.centerForce} min={0} max={0.002} step={0.0001} onChange={v => set('centerForce', v)} />
          <Slider label="Dämpfung" value={settings.damping} min={0.85} max={0.99} step={0.005} onChange={v => set('damping', v)} />

          <div style={{ margin: '20px 0 14px', borderTop: '1px solid var(--color-divider)', paddingTop: 16 }}>
            <span style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--color-text-tertiary)' }}>
              Anzeige
            </span>
          </div>

          <Slider label="Knotengröße" value={settings.nodeSize} min={0.3} max={3} step={0.1} onChange={v => set('nodeSize', v)} />
          <Slider label="Kantenstärke" value={settings.lineWidth} min={0.2} max={3} step={0.1} onChange={v => set('lineWidth', v)} />
          <Slider label="Text-Transparenz" value={settings.textOpacity} min={0.1} max={1} step={0.05} onChange={v => set('textOpacity', v)} />
        </div>
      )}
    </div>
  );
}

// PERF: React.memo verhindert Re-Render bei unveränderten Props (Sidebar-Collapse etc.)
export default React.memo(GraphView);
